
import { supabase } from '@/integrations/supabase/client';
import { ProjectTag, ProjectTagAssignment } from './workbook-types';
import { mapDbTagToProjectTag, mapProjectTagToDbTag, mapDbTagAssignmentToProjectTagAssignment, mapProjectTagAssignmentToDbTagAssignment } from './mappers';

/**
 * Fetches all tags for the current user
 */
export async function getTags(): Promise<ProjectTag[]> {
  const { data, error } = await supabase
    .from('workbook_tags')
    .select('*')
    .order('name');
  
  if (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
  
  return (data || []).map(dbTag => mapDbTagToProjectTag(dbTag));
}

/**
 * Creates a new tag
 */
export async function createTag(tag: Omit<ProjectTag, 'id' | 'createdAt' | 'userId'>): Promise<ProjectTag> {
  // Get current user session
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to create tags');
  }
  
  const dbTag = {
    name: tag.name,
    color: tag.color,
    user_id: session.user.id,
  };
  
  const { data, error } = await supabase
    .from('workbook_tags')
    .insert(dbTag)
    .select()
    .single();
  
  if (error) {
    console.error('Error creating tag:', error);
    throw error;
  }
  
  return mapDbTagToProjectTag(data);
}

/**
 * Updates an existing tag
 */
export async function updateTag(id: string, updates: Partial<ProjectTag>): Promise<ProjectTag> {
  // Convert from ProjectTag model to DB model
  const dbUpdates: any = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.color !== undefined) dbUpdates.color = updates.color;
  
  const { data, error } = await supabase
    .from('workbook_tags')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    console.error('Error updating tag:', error);
    throw error;
  }
  
  return mapDbTagToProjectTag(data);
}

/**
 * Deletes a tag
 */
export async function deleteTag(id: string): Promise<void> {
  const { error } = await supabase
    .from('workbook_tags')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting tag:', error);
    throw error;
  }
}

/**
 * Gets all tag assignments for a project
 */
export async function getProjectTags(projectId: string): Promise<ProjectTag[]> {
  const { data, error } = await supabase
    .from('workbook_tag_assignments')
    .select('*, workbook_tags(*)')
    .eq('workbook_id', projectId);
  
  if (error) {
    console.error('Error fetching project tags:', error);
    throw error;
  }
  
  // Extract the actual tags from the assignments
  const tags = data.map(assignment => mapDbTagToProjectTag(assignment.workbook_tags)) || [];
  
  return tags;
}

/**
 * Assigns a tag to a project
 */
export async function assignTagToProject(tagId: string, projectId: string): Promise<ProjectTagAssignment> {
  // First check if the assignment already exists
  const { data: existing } = await supabase
    .from('workbook_tag_assignments')
    .select('*')
    .eq('tag_id', tagId)
    .eq('workbook_id', projectId)
    .maybeSingle();

  // If it already exists, return it
  if (existing) {
    return mapDbTagAssignmentToProjectTagAssignment(existing);
  }

  // Otherwise create a new assignment
  const dbAssignment = mapProjectTagAssignmentToDbTagAssignment({
    tagId,
    pageId: projectId
  });
  
  const { data, error } = await supabase
    .from('workbook_tag_assignments')
    .insert(dbAssignment)
    .select()
    .single();
  
  if (error) {
    console.error('Error assigning tag to project:', error);
    throw error;
  }
  
  return mapDbTagAssignmentToProjectTagAssignment(data);
}

/**
 * Removes a tag from a project
 */
export async function removeTagFromProject(tagId: string, projectId: string): Promise<void> {
  const { error } = await supabase
    .from('workbook_tag_assignments')
    .delete()
    .eq('tag_id', tagId)
    .eq('workbook_id', projectId);
  
  if (error) {
    console.error('Error removing tag from project:', error);
    throw error;
  }
}
